import { serializeDateFields } from 'common/utils/time';
import { PaginatedApiPayloadV2 } from 'domain/Common';
import {
  EventDetails,
  EventDetailsApiResponse,
  EventHostApiResponse,
  EventHostType,
  EventListItem,
  EventListItemApiResponse,
  PublicEventDetails,
  PublicEventDetailsApiResponse,
  SimilarEvent,
  SimilarEventApiResponse,
  SimilarEventQuestion,
  SimilarEventQuestionApiResponse,
  TranscriptionMode,
} from 'domain/event';
import { EventSeries, EventSeriesApiResponse } from 'domain/event/EventSeries';
import { DEFAULT_NATTERING_DURATION } from 'event/constants';
import { pick } from 'lodash-es';
import flow from 'lodash/fp/flow';
import { transformPaginatedApiPayload } from 'modules/api/utils';

import {
  mapEventRecordingFromApi,
  mapEventRelatedVideoFromApi,
  mapEventTimeFromApi,
  mapOwnerBaseFromApi,
} from './common';
import { mapEventRelatedImagesToEventImage, mapEventRelatedImagesToSponsors } from './eventImage';

export const mapPublicEventFromApi = (data: PublicEventDetailsApiResponse): PublicEventDetails => ({
  id: data.id,
  communityId: data.communityId,
  communitySlug: data.communitySlug,
  communityName: data.communityName,
  hash: data.hash,
  name: data.name,
  description: data.description,
  brandingColor: data.brandingColor,
  logoImageLink: data.logoImageLink,
  status: data.state,
  owner: mapOwnerBaseFromApi(data),
  eventImage: mapEventRelatedImagesToEventImage(data.eventRelatedImages),
  sponsors: mapEventRelatedImagesToSponsors(data.eventRelatedImages),
  eventTime: mapEventTimeFromApi({ id: data.id, startTime: data.startTime, durationInMinutes: data.durationMinutes }),
  isPinEnabled: data.pinEnabled,
});

const mapEventListItemFromApi = (data: EventListItemApiResponse): EventListItem => ({
  ...pick(data, [
    'id',
    'communityId',
    'hash',
    'name',
    'description',
    'state',
    'deletionDate',
    'agoraProxyMode',
    'pin',
    'hosts',
  ]),
  isOwned: data.owned,
  owner: mapOwnerBaseFromApi(data),
  status: data.state,
  eventTime: mapEventTimeFromApi({ id: data.id, startTime: data.startDate, durationInMinutes: data.eventDuration }),
  eventImage: mapEventRelatedImagesToEventImage(data.eventRelatedImages),
  eventRelatedVideos: data.eventRelatedVideos.map(mapEventRelatedVideoFromApi),
  guestCount: data.eventGuestCount,
  isPinEnabled: data.isPinEnabled,
  isAgoraCloudRecordingEnabled: data.isAgoraCloudRecordingEnabled,
  seriesId: data.seriesId ?? undefined,
});

export const mapPaginatedEventListItemsFromApi = flow(
  transformPaginatedApiPayload<EventListItemApiResponse>,
  (payload) => ({
    ...payload,
    data: payload.data.map(flow(serializeDateFields('startDate'), mapEventListItemFromApi)),
  })
);

export const mapEventDetailsFromApi = (data: EventDetailsApiResponse): EventDetails => ({
  ...pick(data, [
    'id',
    'owner',
    'communityId',
    'hash',
    'name',
    'description',
    'deletionDate',
    'agoraProxyMode',
    'pin',
    'hasCompletedPreEventQuestions',
    'hasCompletedPostEventQuestions',
    'isUserRegistered',
  ]),
  isOwned: data.owned,
  eventTime: mapEventTimeFromApi({ id: data.id, startTime: data.startDate, durationInMinutes: data.eventDuration }),
  status: data.state,
  eventImage: mapEventRelatedImagesToEventImage(data.eventRelatedImages),
  sponsors: mapEventRelatedImagesToSponsors(data.eventRelatedImages),
  eventRelatedVideos: data.eventRelatedVideos.map(mapEventRelatedVideoFromApi),
  eventRecordings: data.eventRecordings.map(mapEventRecordingFromApi),
  isPinEnabled: data.isPinEnabled,
  isAgoraCloudRecordingEnabled: data.isAgoraCloudRecordingEnabled,
  hosts: data.hosts?.map(mapHostFromApi) ?? [],
  seriesId: data.seriesId ?? undefined,
  defaultPace: data.defaultPace ?? DEFAULT_NATTERING_DURATION,
  defaultTranscriptionMode: data.defaultTranscriptionMode ?? TranscriptionMode.OPTIONAL,
});

export const mapEventSeriesDetailsFromApi = (data: EventSeriesApiResponse): EventSeries => ({
  id: data.id,
  status: data.state,
  communityId: data.communityId,
  name: data.name,
  description: data.description,
  eventImage: mapEventRelatedImagesToEventImage(data.relatedImages),
  events: data.events.map((event) => ({ ...event, startTime: new Date(event.startTime).getTime() })),
  hosts: data.hosts.map(mapHostFromApi),
  isAgoraCloudRecordingEnabled: data.isAgoraCloudRecordingEnabled,
  isPinEnabled: data.isPinEnabled,
  relatedVideos: data.relatedVideos.map(mapEventRelatedVideoFromApi),
  sponsors: mapEventRelatedImagesToSponsors(data.relatedImages),
  defaultPace: data.defaultPace ?? DEFAULT_NATTERING_DURATION,
  defaultTranscriptionMode: data.defaultTranscriptionMode ?? TranscriptionMode.OPTIONAL,
});

export const mapPaginatedSimilarEventsFromApi = (
  payload: PaginatedApiPayloadV2<SimilarEventApiResponse>
): PaginatedApiPayloadV2<SimilarEvent> => ({
  ...payload,
  data: payload.data.map(mapSimilarEventsFromApi),
});

const mapSimilarEventsFromApi = (data: SimilarEventApiResponse): SimilarEvent => ({
  ...pick(data, ['id', 'owner', 'communityId', 'hash', 'name', 'description', 'deletionDate', 'agoraProxyMode', 'pin']),
  eventTime: mapEventTimeFromApi({ id: data.id, startTime: data.startDate, durationInMinutes: data.eventDuration }),
  status: data.state,
  questions: data.questions.map(mapSimilarEventQuestionFromApi),
  eventRelatedVideos: data.eventRelatedVideos.map(mapEventRelatedVideoFromApi),
  guestCount: data.eventGuestCount,
  isAgoraCloudRecordingEnabled: data.isAgoraCloudRecordingEnabled,
});

const mapSimilarEventQuestionFromApi = (data: SimilarEventQuestionApiResponse): SimilarEventQuestion => data;

export const mapEventDetailsToEventListItem = (eventDetails: EventDetails): EventListItem => ({
  ...eventDetails,
  guestCount: 0,
  hosts: eventDetails.hosts.map(({ accountId }) => accountId),
});

export const mapHostFromApi = (host: EventHostApiResponse): EventHostType => ({
  accountId: host.accountId,
  communityRole: host.communityRole,
  email: host.email,
  avatarLink: host.avatarLink,
  role: host.role,
  name: host.fullName,
});
